import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Trash2, AlertTriangle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function CleanupPage() {
  const { authAxios } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const api = authAxios();
      const res = await api.get('/cleanup');
      setItems(res.data);
    } catch (err) {
      toast.error('Failed to fetch cleanup items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="w-8 h-8 text-[#EF694C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold font-heading text-white tracking-tight">
          Cleanup Queue
        </h1>
        <p className="text-sm text-[#a1a1aa] mt-1">
          {items.length} domains require attention
        </p>
      </div>

      <Card className="bg-[#121214] border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-[#a1a1aa]">Domain</TableHead>
                <TableHead className="text-[#a1a1aa]">Status</TableHead>
                <TableHead className="text-[#a1a1aa]">Owner</TableHead>
                <TableHead className="text-[#a1a1aa]">Suggested Action</TableHead>
                <TableHead className="text-right text-[#a1a1aa]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.fqdn} className="border-white/5 hover:bg-white/[0.03]">
                  <TableCell className="font-mono text-white">
                    {item.fqdn}
                  </TableCell>
                  <TableCell>
                    <Badge className="bg-red-500/10 text-red-500 border-red-500/20">
                      {item.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[#a1a1aa]">
                    {item.owner}
                  </TableCell>
                  <TableCell className="text-[#a1a1aa]">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-500" />
                      {item.suggested_action}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" className="hover:bg-white/10">
                      Ignore
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-[#52525b]">
                    <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500/50" />
                    <p>Everything looks clean!</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
